import React, {useMemo, useState} from "react";
import Box from "@mui/material/Box";
import ButtonCreate from "../../../components/UI/button/ButtonCreate";
import {
    MaterialReactTable,
    MRT_GlobalFilterTextField,
    MRT_ToggleFiltersButton,
    MRT_ToolbarAlertBanner
} from "material-react-table";
import MenuItem from "@mui/material/MenuItem";
import {MRT_Localization_RU} from "material-react-table/locales/ru";
import {SideBaseModule} from "../../../data/SideBaseModule";
import {HeightLocationModule} from "../../../data/HeightLocationModule";
import {Link} from "react-router-dom";
import {withCompositionIdQuery} from "../../../utils/withCompositionIdQuery";
import ModalModule from "../../../components/UI/modal/ModalModule";
import CompositionController from "../../../API/organization/CompositionController";
import {
    buildOrganizationCompositionModulePatchBodyFromAddForm,
} from "../../../utils/mapModuleCompositionToModuleItem";
import OrderUtil from "../../../utils/orders/OrderUtil";
import {comparePriority} from "../../../utils/comparePriority";
import OverflowTooltip from "../../../components/UI/tooltip/OverflowTooltip";
import {
    COMPOSITION_MODULES_TABLE_COL_WIDTHS,
    COMPOSITION_MODULES_TAB_TABLE_WIDTH,
    compositionModulesTabTableScrollSx,
} from "./compositionModulesTabTableLayout";

const rowInitValue = {
    width: "", height: "", depth: "", "coordinateXPoint": 0,
    "coordinateYPoint": 0, sideBaseModule: null
}

const columnCellSx = (columnId) => {
    const width = COMPOSITION_MODULES_TABLE_COL_WIDTHS[columnId]
    if (width == null) {
        return {}
    }
    return {
        boxSizing: 'border-box',
        whiteSpace: 'nowrap',
    }
}

const columnBodyCellSx = (columnId) => {
    const base = columnCellSx(columnId)
    if (columnId === 'name') {
        return {
            ...base,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
        }
    }
    return base
}

const COLUMN_ORDER = [
    'priority',
    'name',
    'width',
    'height',
    'depth',
    'sideBaseModule',
    'heightLocation',
    'coordinateXPoint',
    'coordinateYPoint',
]

const CompositionModuleTable = ({
                                    handleModalAlert,
                                    data,
                                    setData,
                                    tableOptions,
                                    tableOptionsHandle,
                                    globalFilter,
                                    handleSearchQuery,
                                    filteredData,
                                    columnsFilter,
                                    enableRowActions = false,
                                    compositionId,
                                    compositionName,
                                }) => {
    const sideBaseModuleList = Object.entries(SideBaseModule)
    const [modalOpen, setModalOpen] = useState(false);
    const [loading, setLoading] = useState(false)
    const [showColumnFilters, setShowColumnFilters] = useState(false)
    const [currentEditRowData, setCurrentEditRowData] = useState(rowInitValue)
    const rows = Array.isArray(data) ? data : []
    const tableRows = Array.isArray(filteredData) ? filteredData : []

    const nameList = useMemo(() => OrderUtil.getListField(rows, 'name').filter(Boolean), [rows]);
    const sideBaseModuleFilterList = useMemo(
        () => OrderUtil.getListFieldByEnum(rows, 'sideBaseModule', SideBaseModule),
        [rows],
    );
    const heightLocationFilterList = useMemo(
        () => OrderUtil.getListFieldByEnum(
            rows.map((row) => ({heightLocation: row.module?.heightLocation})),
            'heightLocation',
            HeightLocationModule,
        ),
        [rows],
    );

    const handleShowColumnFilters = () => {
        setShowColumnFilters(!showColumnFilters)
        if (showColumnFilters) {
            handleSearchQuery([], data, null, true)
        }
    }

    const columns = useMemo(() => {
        const baseColumns = [
            {
                id: 'priority',
                header: 'Приоритет',
                accessorKey: 'priority',
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.priority,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.priority,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.priority,
                grow: false,
                enableResizing: false,
                sortingFn: (rowA, rowB) => comparePriority(rowA.original.priority, rowB.original.priority),
            },
            {
                id: 'name',
                header: 'Наименование',
                accessorKey: 'name',
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.name,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.name,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.name,
                grow: false,
                enableResizing: false,
                filterVariant: 'multi-select',
                filterSelectOptions: nameList,
                muiFilterTextFieldProps: {
                    placeholder: 'Отфильтровать по наименованию',
                    sx: {width: '280px'},
                },
                Cell: ({row, renderedCellValue}) => (
                    <OverflowTooltip title={row.original.name}>
                        <Link to={withCompositionIdQuery(
                            '/organization/module-composition/' + row.original.id,
                            compositionId,
                            compositionName,
                        )}>
                            {renderedCellValue}
                        </Link>
                    </OverflowTooltip>
                ),
            },
            {
                id: 'width',
                header: 'Ширина',
                accessorFn: (row) => row.module?.width ?? row.width,
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.width,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.width,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.width,
                grow: false,
                enableResizing: false,
            },
            {
                id: 'height',
                header: 'Высота',
                accessorFn: (row) => row.module?.height ?? row.height,
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.height,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.height,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.height,
                grow: false,
                enableResizing: false,
            },
            {
                id: 'depth',
                header: 'Глубина',
                accessorFn: (row) => row.module?.depth ?? row.depth,
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.depth,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.depth,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.depth,
                grow: false,
                enableResizing: false,
            },
            {
                id: 'sideBaseModule',
                header: 'Расположение',
                accessorFn: (row) => SideBaseModule[row.sideBaseModule] ?? '',
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.sideBaseModule,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.sideBaseModule,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.sideBaseModule,
                grow: false,
                enableResizing: false,
                filterVariant: 'multi-select',
                filterSelectOptions: sideBaseModuleFilterList,
                muiFilterTextFieldProps: {
                    placeholder: 'Отфильтровать по расположению',
                    sx: {width: '220px'},
                },
            },
            {
                id: 'heightLocation',
                header: 'Верх/Низ',
                accessorFn: (row) => HeightLocationModule[row.module?.heightLocation] ?? '',
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.heightLocation,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.heightLocation,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.heightLocation,
                grow: false,
                enableResizing: false,
                filterVariant: 'multi-select',
                filterSelectOptions: heightLocationFilterList,
                muiFilterTextFieldProps: {
                    placeholder: 'Отфильтровать по верх/низ',
                    sx: {width: '180px'},
                },
            },
            {
                id: 'coordinateXPoint',
                header: 'X',
                accessorKey: 'coordinateXPoint',
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.coordinateXPoint,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.coordinateXPoint,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.coordinateXPoint,
                grow: false,
                enableResizing: false,
            },
            {
                id: 'coordinateYPoint',
                header: 'Y',
                accessorKey: 'coordinateYPoint',
                size: COMPOSITION_MODULES_TABLE_COL_WIDTHS.coordinateYPoint,
                minSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.coordinateYPoint,
                maxSize: COMPOSITION_MODULES_TABLE_COL_WIDTHS.coordinateYPoint,
                grow: false,
                enableResizing: false,
            },
        ]

        return baseColumns;

    }, [nameList, sideBaseModuleFilterList, heightLocationFilterList, compositionId, compositionName]);

    const handleSubmit = (moduleId, value, close, catalogModule = null) => {
        setLoading(true)
        const patchBody = buildOrganizationCompositionModulePatchBodyFromAddForm(
            value,
            catalogModule ?? {},
        );
        if (patchBody == null) {
            setLoading(false);
            handleModalAlert({message: "Не удалось собрать тело запроса"});
            return;
        }
        CompositionController.module(compositionId, moduleId, patchBody)
            .then(function (response) {
                const newData = [...data, response.data]
                setData(newData)
                handleSearchQuery(globalFilter, newData)
                handleModalAlert("Модуль добавлен", 'success');
                close()
            })
            .catch(function (error) {
                handleModalAlert(error);
            }).finally(() => setLoading(false))
    }

    const handleEdit = (values, close) => {
    }
    const handleEditRow = (row) => {
        // setModalOpen(true)
        // setCurrentEditRowData(row)
    }
    const handleClose = () => {
        setModalOpen(false)
        setCurrentEditRowData(rowInitValue)
    }
    const menuItem = [
        {type: "edit", text: "Изменить", handler: handleEditRow},
    ]
    return (<Box sx={compositionModulesTabTableScrollSx}>
        <Box sx={{
            width: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
            minWidth: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
        }}>
        <Box
            sx={{display: 'flex', justifyContent: "flex-end", paddingBottom: "30px"}}
        >
            <ButtonCreate
                text={"Добавить"}
                width={140}
                onClick={() => setModalOpen(true)}
            />
        </Box>
        <MaterialReactTable
            renderTopToolbar={({table}) => {
                return <Box sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "7px",
                    width: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
                    boxSizing: 'border-box',
                    backgroundColor: (theme) => table.getSelectedRowModel().rows.length > 0 ? theme.palette.primary.light : "transparent",
                }}>
                    <MRT_ToolbarAlertBanner table={table}/>
                    <Box sx={{display: "flex"}}>
                        <MRT_ToggleFiltersButton table={table}/>
                        <MRT_GlobalFilterTextField table={table}/>
                    </Box>
                </Box>
            }}
            muiTablePaperProps={{
                sx: {
                    width: 'fit-content',
                    maxWidth: 'fit-content',
                },
            }}
            muiTableContainerProps={{
                sx: {
                    width: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
                    maxWidth: COMPOSITION_MODULES_TAB_TABLE_WIDTH,
                    maxHeight: 'calc(100vh - 420px)',
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    scrollbarGutter: 'stable',
                },
            }}
            muiTableProps={{
                sx: {
                    width: '100%',
                    tableLayout: 'fixed',
                    borderCollapse: 'collapse',
                },
            }}
            muiTableBodyCellProps={({column}) => ({
                sx: {
                    p: column.id === 'coordinateYPoint' ? '0px 12px 0px 10px' : '0px 0px 0px 10px',
                    m: 0,
                    border: "none",
                    "&:after": {
                        display: "none",
                    },
                    "&:not(:last-of-type)": {
                        borderRight: '1px solid #EAEBEA',
                    },
                    "& a": {
                        color: "#006CCC",
                        textDecoration: "none",
                        "&:hover": {
                            textDecoration: "underline"
                        }
                    },
                    ...columnBodyCellSx(column.id),
                },
            })}
            muiTableHeadCellProps={({column}) => ({
                sx: {
                    border: 'none',
                    borderRight: '1px solid #EAEBEA',
                    borderBottom: '1px solid #EAEBEA',
                    backgroundColor: "#F5F6FA",
                    padding: "10px",
                    paddingBottom: " 0.2rem",
                    paddingTop: "0.2rem",
                    whiteSpace: 'nowrap',
                    ...columnCellSx(column.id),
                    ...(column.id === 'coordinateYPoint' ? {paddingRight: '12px'} : {}),
                    '& .Mui-TableHeadCell-Content': {
                        flexWrap: 'nowrap',
                        minWidth: 0,
                    },
                    '& .Mui-TableHeadCell-Content-Labels, & .Mui-TableHeadCell-Content-Wrapper': {
                        overflow: 'hidden',
                        textOverflow: 'clip',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                    },
                },
            })}
            muiTableBodyRowProps={({row}) => {
                return ({
                    sx: () => ({
                        height: '36px',
                        backgroundColor: "white",
                        "&:hover& td:after": {
                            display: "none",
                        },
                        "&.MuiTableRow-root.Mui-selected": {
                            backgroundColor: (theme) => theme.palette.primary.light70,
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.primary.light50,
                            },
                        },
                        "&:hover": {
                            "& td:after": {
                                display: "none",
                            },
                            backgroundColor: (theme) => theme.palette.primary.stroke,
                        },
                        '&:nth-of-type(even)': {
                            "&.MuiTableRow-root.Mui-selected": {
                                backgroundColor: (theme) => theme.palette.primary.light,
                                "&:hover": {
                                    backgroundColor: (theme) => theme.palette.primary.light50,
                                },
                            },
                            backgroundColor: (theme) => theme.palette.primary.lightGrey,
                            "&:hover": {
                                backgroundColor: (theme) => theme.palette.primary.stroke,
                            },
                        },
                    })
                })
            }}

            displayColumnDefOptions={{
                'mrt-row-actions': {
                    header: "",
                    size: 3,
                    minSize: 3,
                    grow: false,
                },
            }}
            muiSearchTextFieldProps={{
                placeholder: `Поиск`,
                sx: {
                    paddingTop: "5px",
                    paddingBottom: "0px",
                    "& .MuiCollapse-root": {
                        borderRadius: '12px',
                        height: 40, width: 290,
                        "& .MuiSvgIcon-root": {
                            color: "#737478"
                        },
                        '& .MuiOutlinedInput-input': {
                            padding: 0
                        }
                    }
                },
                variant: 'outlined',
            }}
            columns={columns}
            data={tableRows}
            initialState={{
                sorting: [{id: 'priority', desc: false}],
                columnOrder: COLUMN_ORDER,
            }}
            rowHeight={13}
            columnSpacing={0}
            getRowId={(originalRow) => originalRow.id}
            selectionPadding={1}
            onPaginationChange={(updater) => {
                tableOptionsHandle(updater(tableOptions));
            }}
            onGlobalFilterChange={(value) => {
                handleSearchQuery(value, data, null)
            }}
            onColumnFiltersChange={(updater) => {
                handleSearchQuery(updater(columnsFilter), data, null, true)
            }}
            onShowColumnFiltersChange={handleShowColumnFilters}
            manualFiltering={true}
            state={{
                showColumnFilters: showColumnFilters,
                columnFilters: columnsFilter,
                globalFilter: globalFilter,
                showGlobalFilter: true,
                pagination: tableOptions,
            }}
            rowCount={tableOptions.totalElements}
            selectionCss={{
                backgroundColor: 'lightblue',
                borderWidth: '2px',
                borderStyle: 'solid',
                borderColor: 'blue',
                borderRadius: '4px',
            }}
            renderRowActionMenuItems={({closeMenu, row}) => menuItem.map(el => {
                return (el.type === "edit" || (el.type === "delete" && !row.original.blocked)) && <MenuItem
                    key={el.type}
                    onClick={() => {
                        el.handler(row.original)
                        closeMenu();
                    }}
                    sx={{
                        margin: "0 8px",
                        borderRadius: "4px",
                        "&:hover": {backgroundColor: (theme) => theme.palette.primary.light}
                    }}
                >
                    {el.text}
                </MenuItem>
            })
            }
            localization={MRT_Localization_RU}
            enableColumnActions={false}
            enableRowActions={enableRowActions}
            enableToolbarInternalActions={false}
        />
        </Box>
        {modalOpen && <ModalModule
            title={currentEditRowData.id ? "Изменить" : "Добавить"}
            open={modalOpen} onClose={handleClose}
            handleSubmit={handleSubmit}
            initValue={currentEditRowData}
            loading={loading}
            composition={true}
            sideBaseModuleList={sideBaseModuleList}
            handleModalAlert={handleModalAlert}
        />}
    </Box>)
}
export default CompositionModuleTable